import React, {Fragment} from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import MUIGrid from '../MUI-Grid';
import MUITypography from '../MUI-Typography';
import MUIContainer from '../MUI-Container';
import MUIList from '../MUIList';
import MUIListItem from '../MUIList/MUIListItem';
import parse from 'html-react-parser';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import {colors, fontWeight, lineHeightRatio} from '@/styles/tokens';

// Migrated from `.faq-inner-wrapper .answer` in globals.scss (line 2651, `a:hover` underline)
// and questions.scss (line 19, line-height + letter-spacing on the same nested selector — the
// same class is split across the two partials, both compiled into one bundle). Sole owner
// confirmed: no other page/component renders an element with this className; `.answer` here only
// ever matches this component's own AccordionDetails typography, which authors the literal
// `answer` className directly in this file's JSX below. `lineHeightRatio.normal` (1.5) is an exact
// token match for the unitless line-height; `letterSpacing` has no exact token match — preserved
// for parity. No `!important` on letter-spacing, matching source (the original questions.scss
// comment notes `.faq-inner-wrapper .answer`'s own specificity already outranks MUI Typography's
// letter-spacing without it).
const faqInnerWrapperSx = {
    '& .answer': {
        lineHeight: lineHeightRatio.normal,
        letterSpacing: '0.00938em', // no exact token match — preserved for parity
        '& a:hover': {
            textDecoration: 'underline !important'
        }
    }
} as const;

// Migrated from `.remove-icon` in questions.scss (line 27). Sole owner confirmed: only this
// component's expanded-state RemoveIcon carries this className. `colors.text.white` is an exact
// token match for `#ffffff`.
const removeIconSx = {
    color: `${colors.text.primary} !important`
} as const;

// Migrated from `.question` in questions.scss (lines 7-17). Source is a compound selector list —
// `.question, .answer { font-size: 16px !important; font-weight: 400 !important; text-align: left
// !important; }` — followed by a `.question`-only block overriding font-size to 17.5px and
// bolding nested spans. `.answer` in that shared selector is NOT this component's to own (it's
// also used outside this component, e.g. src/pages/privacy-policy/index.tsx, plus other elements
// inside this same file) so it is left untouched/global — only `.question`'s own resolved cascade
// is reproduced here: 17.5px wins over 16px (equal specificity, both `!important`, later source
// order wins), and font-weight/text-align only ever come from the shared rule since the
// `.question`-only block doesn't redeclare them. `fontWeight.regular` (400) and `fontWeight.bold`
// (700, for the nested `span`) are exact token matches. No token exists for `text-align`; 17.5px
// has no exact match on the fontSize scale — both preserved as literals for parity. (A page-scoped
// override for `.question`/`.answer` also exists in src/styles/career.module.scss, scoped to the
// Career page's work-culture accordion; its higher selector specificity continues to win over
// this sx unchanged, same as it did over the plain CSS class today — out of scope here.)
const questionSx = {
    fontSize: '17.5px !important', // no exact token match — preserved for parity
    fontWeight: `${fontWeight.regular} !important`,
    textAlign: 'left !important', // no token for text-align — preserved for parity
    '& span': {
        fontWeight: `${fontWeight.bold} !important`
    }
} as const;

// Migrated from globals.scss `.answer-detail-wrapper` (this component's own AccordionDetails —
// `.font-h3`/`.font-h4` from the same source rule are NOT included: only src/pages/privacy-policy
// independently renders those, not this component; migrated separately there). Every nested
// selector here (`.answer-detail-header`, `.answer-detail-footer`, `.sub-answer-wrapper`,
// `.answer-sub-heading`, `.answer-key-head`, `.answer-key-body`) is this component's own
// descendant, authored directly in this file's JSX below — sole owner.
const answerDetailWrapperSx = {
    textAlign: 'left',
    '& .answer-detail-header a, & .answer-detail-footer a': {
        textDecoration: 'none !important'
    },
    '& .answer-detail-header a:hover, & .answer-detail-footer a:hover': {
        textDecoration: 'underline !important'
    },
    '& .sub-answer-wrapper': {
        margin: '12px 0 !important'
    },
    '& .answer-sub-heading': {
        fontFamily: "'Headers', sans-serif !important",
        fontWeight: '700 !important',
        fontSize: '20px !important',
        marginBottom: '8px !important'
    },
    '& .answer-key-head': {
        fontFamily: "'Headers', sans-serif !important",
        fontSize: '18px !important'
    },
    '& .answer-key-body': {
        marginBottom: '8px !important',
        '& span': {
            fontWeight: 'bold !important'
        }
    }
} as const;

export default function SimpleAccordion(props: any) {
    const {accordianData, isIconVisible, className, accheadClassName, subpart, removeIconClass} = props;
    const [expanded, setExpanded] = React.useState<string | false>('panel');

    const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? panel : false);
    };
    return (
        <Box className="faq-inner-wrapper" sx={faqInnerWrapperSx}>
            <MUIGrid container spacing={isIconVisible ? 2 : 1}>
                {accordianData?.map((data: any, index: number) => (
                    <MUIGrid item xs={12} key={index} className="accordion-grid-main">
                        <Accordion
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            className={accheadClassName}
                            sx={{boxShadow: '0 0 16px 5px rgba(0, 0, 0, 0.15)', border: '1px solid rgba(0, 0, 0, 0.15)'}}
                        >
                            <AccordionSummary
                                expandIcon={
                                    expanded === `panel${index}` ? (
                                        <RemoveIcon className={`remove-icon ${removeIconClass}`} sx={removeIconSx} />
                                    ) : (
                                        <AddIcon />
                                    )
                                }
                                aria-controls="panel1a-content"
                                id={`panel${index}a-header`}
                                // className="panel1a-header"
                                className={className}
                            >
                                <MUITypography className="question font-regular-text" component="h3" sx={questionSx}>
                                    {data.question ? parse(data.question) : ''}
                                </MUITypography>
                            </AccordionSummary>
                            {subpart && typeof data.answer !== 'string' ? (
                                <AccordionDetails className="muiAccordionDetails answer-detail-wrapper" sx={answerDetailWrapperSx}>
                                    <MUITypography className="answer-detail-header data_links ">
                                        {data?.answer?.header ? parse(data?.answer?.header) : ''}
                                    </MUITypography>
                                    {data?.answer?.subpart?.map((answerData: any, answerIndex: number) => (
                                        <Box key={answerIndex} className="sub-answer-wrapper">
                                            <MUITypography className="answer-sub-heading font-regular-text">
                                                {answerData?.heading}
                                            </MUITypography>
                                            {answerData?.keypoints?.map((subdata: any, keypointsIndex: number) => (
                                                <Fragment key={keypointsIndex}>
                                                    <MUIContainer>
                                                        <MUITypography className="answer-key-head">{subdata?.keyHeading}</MUITypography>
                                                        <MUIList component="ol" className="answer-list-wrapper">
                                                            <MUIListItem disablePadding={true} className="answer-list-items">
                                                                <MUITypography className="answer-key-body data_links answer">
                                                                    {subdata?.keyBody ? parse(subdata?.keyBody) : ''}
                                                                </MUITypography>
                                                            </MUIListItem>
                                                        </MUIList>
                                                    </MUIContainer>
                                                </Fragment>
                                            ))}
                                        </Box>
                                    ))}
                                    <Typography className="answer-detail-footer data_links answer">
                                        {data?.answer?.body ? parse(data?.answer?.body) : ''}
                                    </Typography>
                                    <MUITypography className="answer-detail-footer">
                                        {data?.answer?.footer ? parse(data?.answer?.footer) : ''}
                                    </MUITypography>
                                </AccordionDetails>
                            ) : (
                                <AccordionDetails className="muiAccordionDetails">
                                    <Typography className="data_links answer font-regular-text">
                                        {data?.answer ? parse(data.answer) : ''}
                                    </Typography>
                                </AccordionDetails>
                            )}
                        </Accordion>
                    </MUIGrid>
                ))}
            </MUIGrid>
        </Box>
    );
}
